/*
 * File: SelectInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/18/2023
 * Copyright (c) DrivingDoc 2023
 */
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';

interface PropTypes extends Omit<OutlinedTextFieldProps, 'variant'> {
  name: string;
  label: string;
  options?: { label: string; value: string }[];
}

export default function SelectInput({ name, label, options, ...props }: PropTypes) {
  const { control } = useFormContext();
  return (
    <Box>
      {label && (
        <Typography
          component={InputLabel}
          htmlFor={name}
          variant="subtitle1"
          color="text.primary"
          fontWeight={700}
          disabled={props.disabled}
        >
          {label}
        </Typography>
      )}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <TextField
            select
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            inputRef={field.ref}
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onChange={(event) => {
              field.onChange(event.target.value);
            }}
            slotProps={{
              input: {
                name: field.name,
              },
            }}
            {...props}
          >
            {options &&
              options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
        )}
      />
    </Box>
  );
}
